import React, { useState } from 'react';
import { Avatar, Dialog, DialogContent, IconButton, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FlipIcon from '@mui/icons-material/Flip';

export const HoverableAvatar = styled(Avatar)({
  transition: 'transform 0.2s ease',
  cursor: 'pointer',
  '&:hover': { transform: 'scale(1.2)' },
});

export function ProfileAvatar({
  src,
  alt,
  fallback,
  sx,
}: {
  src?: string;
  alt?: string;
  fallback?: string;
  sx?: any;
}) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);

  const handleOpen = () => {
    setOpen(true);
    setScale(1);
    setRotation(0);
    setFlipped(false);
    setDrag({ x: 0, y: 0 });
  };

  const handleClose = () => setOpen(false);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setScale((prev) => Math.max(0.1, prev + (e.deltaY > 0 ? -0.1 : 0.1)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDragging(true);
    setStart({ x: e.clientX - drag.x, y: e.clientY - drag.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragging && start) {
      setDrag({ x: e.clientX - start.x, y: e.clientY - start.y });
    }
  };

  const handleMouseUp = () => setDragging(false);

  return (
    <>
      <HoverableAvatar
        src={src || ''}
        alt={alt || ''}
        sx={sx}
        onClick={handleOpen}
      >
        {!src ? fallback || '?' : ''}
      </HoverableAvatar>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            background: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            height: '100vh',
            overflow: 'visible',
            background: 'transparent',
          }}
          onWheel={handleWheel}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <Box
            component="img"
            src={src || ''}
            alt={alt || ''}
            onMouseDown={handleMouseDown}
            draggable={false}
            sx={{
              cursor: dragging ? 'grabbing' : 'grab',
              transform: `
                translate(-50%, -50%) 
                translate(${drag.x}px, ${drag.y}px)
                scale(${scale})
                rotate(${rotation}deg)
                ${flipped ? 'scaleX(-1)' : ''}
              `,
              transition: dragging ? 'none' : 'transform 0.2s ease',
              userSelect: 'none',
              pointerEvents: 'auto',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transformOrigin: 'center',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1.5,
              bgcolor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(12px)',
              borderRadius: 8,
              p: 1,
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'white',
                transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  bgcolor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setRotation((r) => r - 90);
              }}
              sx={{
                color: 'white',
                transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  bgcolor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <RotateLeftIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setRotation((r) => r + 90);
              }}
              sx={{
                color: 'white',
                transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  bgcolor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <RotateRightIcon />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setFlipped((f) => !f);
              }}
              sx={{
                color: 'white',
                transition: 'all 0.25s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  bgcolor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <FlipIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
